import { MilestoneStatus, PhaseStatus, ProjectHealth, ProjectStatus } from '@kainos/shared';
import type { Milestone, Phase, Project } from '@kainos/validation';
import { DEMO_TENANT_ID } from './session.js';
import { daysAgo } from './time.js';

const audit = {
  createdAt: daysAgo(120),
  createdBy: 'usr_renata',
  updatedAt: daysAgo(1),
  updatedBy: 'usr_renata',
};

/** The four projects from the approved design, in the same order. */
export const DEMO_PROJECTS: Project[] = [
  {
    ...audit,
    id: 'prj_plantanorte',
    tenantId: DEMO_TENANT_ID,
    name: 'Implementación Planta Norte',
    description:
      'Implementación de la nueva línea de proceso en Planta Norte, desde ingeniería de detalle hasta puesta en marcha.',
    status: ProjectStatus.IN_PROGRESS,
    health: ProjectHealth.ATTENTION,
    managerId: 'usr_daniela',
    startDate: '2026-03-03',
    endDate: '2026-11-15',
    progress: 62,
    expectedProgress: 68,
  },
  {
    ...audit,
    id: 'prj_montajeelectrico',
    tenantId: DEMO_TENANT_ID,
    name: 'Montaje Eléctrico Subestación Sur',
    description: 'Montaje eléctrico y energización parcial de la Subestación Sur.',
    status: ProjectStatus.AT_RISK,
    health: ProjectHealth.CRITICAL,
    managerId: 'usr_felipe',
    startDate: '2026-05-12',
    endDate: '2026-12-30',
    progress: 38,
    expectedProgress: 52,
  },
  {
    ...audit,
    id: 'prj_bodegacentral',
    tenantId: DEMO_TENANT_ID,
    name: 'Ampliación Bodega Central',
    description: 'Ampliación de la bodega central y habilitación de nuevas zonas de acopio.',
    status: ProjectStatus.IN_PROGRESS,
    health: ProjectHealth.NORMAL,
    managerId: 'usr_camila',
    startDate: '2026-01-08',
    endDate: '2026-09-10',
    progress: 81,
    expectedProgress: 78,
  },
  {
    ...audit,
    id: 'prj_flotavehicular',
    tenantId: DEMO_TENANT_ID,
    name: 'Renovación Flota Vehicular',
    description: 'Renovación completa de la flota vehicular operativa.',
    status: ProjectStatus.COMPLETED,
    health: ProjectHealth.NORMAL,
    managerId: 'usr_carlos',
    startDate: '2025-10-01',
    endDate: '2026-06-30',
    progress: 100,
    expectedProgress: 100,
  },
];

/** Narrative shown in the AI panel. Written by the pulse service in production. */
export const DEMO_PROJECT_PULSE: Record<string, string> = {
  prj_plantanorte:
    'El proyecto mantiene un avance saludable en general. La fase de Construcción concentra el mayor riesgo: 2 actividades sin actualización y 1 atrasada podrían comprometer el hito Puesta en marcha si no se resuelven esta semana.',
  prj_montajeelectrico:
    'Dos actividades están bloqueadas por falta de sensores de temperatura. Si el proveedor no entrega los materiales en 48 horas, el hito Energización parcial se desplazará al menos 3 días.',
  prj_bodegacentral:
    'Avance por sobre lo esperado. KainOS AI detectó un riesgo potencial en la entrega de materiales que podría afectar el cierre del proyecto.',
  prj_flotavehicular: 'Proyecto cerrado sin observaciones pendientes.',
};

export const DEMO_PHASES: Phase[] = [
  {
    ...audit,
    id: 'phs_ingenieria',
    tenantId: DEMO_TENANT_ID,
    projectId: 'prj_plantanorte',
    name: 'Ingeniería',
    order: 0,
    status: PhaseStatus.COMPLETED,
    progress: 100,
  },
  {
    ...audit,
    id: 'phs_adquisiciones',
    tenantId: DEMO_TENANT_ID,
    projectId: 'prj_plantanorte',
    name: 'Adquisiciones',
    order: 1,
    status: PhaseStatus.COMPLETED,
    progress: 100,
  },
  {
    ...audit,
    id: 'phs_construccion',
    tenantId: DEMO_TENANT_ID,
    projectId: 'prj_plantanorte',
    name: 'Construcción',
    order: 2,
    status: PhaseStatus.IN_PROGRESS,
    progress: 58,
  },
  {
    ...audit,
    id: 'phs_puestaenmarcha',
    tenantId: DEMO_TENANT_ID,
    projectId: 'prj_plantanorte',
    name: 'Puesta en marcha',
    order: 3,
    status: PhaseStatus.NOT_STARTED,
    progress: 0,
  },
];

export const DEMO_MILESTONES: Milestone[] = [
  {
    ...audit,
    id: 'mst_energizacion',
    tenantId: DEMO_TENANT_ID,
    projectId: 'prj_montajeelectrico',
    name: 'Energización parcial',
    dueDate: '2026-09-02',
    status: MilestoneStatus.PLANNED,
  },
  {
    ...audit,
    id: 'mst_entregafinal',
    tenantId: DEMO_TENANT_ID,
    projectId: 'prj_bodegacentral',
    name: 'Entrega final',
    dueDate: '2026-09-10',
    status: MilestoneStatus.AT_RISK,
    projectedSlipDays: 5,
  },
  {
    ...audit,
    id: 'mst_puestaenmarcha',
    tenantId: DEMO_TENANT_ID,
    projectId: 'prj_plantanorte',
    phaseId: 'phs_puestaenmarcha',
    name: 'Puesta en marcha',
    dueDate: '2026-09-20',
    status: MilestoneStatus.AT_RISK,
    projectedSlipDays: 3,
  },
  {
    ...audit,
    id: 'mst_validacion',
    tenantId: DEMO_TENANT_ID,
    projectId: 'prj_montajeelectrico',
    name: 'Validación de sistema',
    dueDate: '2026-10-15',
    status: MilestoneStatus.PLANNED,
  },
];
